import {
  ForemanEmployeeDispatch,
  ForemanEmployeeLabor,
  ForemanJobDetail,
} from '@ups/xplat/api/dto';

/**
 * Canonical key for a foreman job row in the offline jobs array and ITE UI.
 * ADO #30176 — producer dual-keying.
 *
 * Prefer `foremanJobID` (Sparta instance id), then `jobID`. Empty string is
 * treated as missing: `||` is intentional, not style. `??` would keep `""` as a
 * present value and re-split the same logical job under two store conventions.
 *
 * Aligns with `getJobKey` in nativescript-foreman matchers. The offline cache
 * previously used `jobID ?? foremanJobID` (inverted), so the same physical
 * store was written under both conventions with no write-side dedupe.
 */
export function getForemanJobDetailKey(
  job: Partial<ForemanJobDetail> | null | undefined
): string {
  if (!job) {
    return '';
  }
  const foreman =
    job.foremanJobID != null ? String(job.foremanJobID).trim() : '';
  const jobId = job.jobID != null ? String(job.jobID).trim() : '';
  return foreman || jobId || '';
}

/**
 * True when two job records share a non-empty id on the SAME field
 * (both foremanJobID, or both jobID). Not transitive alone: F-only and J-only
 * do not match each other; a bridge record carrying both ids links them via
 * union-find in dedupeForemanJobsByIdentity.
 */
export function jobsShareForemanIdentity(
  a: Partial<ForemanJobDetail> | null | undefined,
  b: Partial<ForemanJobDetail> | null | undefined
): boolean {
  if (!a || !b) {
    return false;
  }
  const aF = a.foremanJobID != null ? String(a.foremanJobID).trim() : '';
  const bF = b.foremanJobID != null ? String(b.foremanJobID).trim() : '';
  if (aF && bF && aF === bF) {
    return true;
  }
  const aJ = a.jobID != null ? String(a.jobID).trim() : '';
  const bJ = b.jobID != null ? String(b.jobID).trim() : '';
  if (aJ && bJ && aJ === bJ) {
    return true;
  }
  return false;
}

function laborHoursOnJob(job: ForemanJobDetail): number {
  let hours = 0;
  for (const employee of job.employees || []) {
    for (const labor of employee.labors || []) {
      // Cleared/inactive rows must not outweigh live edits in the union tiebreak.
      if (labor.bActive === false) {
        continue;
      }
      hours += Number(labor.hours || 0);
    }
  }
  return hours;
}

function identityRichness(job: ForemanJobDetail): number {
  let score = 0;
  if (job.vpJobID) {
    score += 2;
  }
  if (job.name) {
    score += 1;
  }
  if (job.foremanJobID && job.jobID) {
    score += 1;
  }
  return score;
}

function deepCloneJob(job: ForemanJobDetail): ForemanJobDetail {
  if (typeof structuredClone === 'function') {
    return structuredClone(job) as ForemanJobDetail;
  }
  return JSON.parse(JSON.stringify(job)) as ForemanJobDetail;
}

function employeeDispatchKey(employee: ForemanEmployeeDispatch): string {
  return String(
    employee.foremanEmployeeJobID ||
      (employee as { employeeID?: string }).employeeID ||
      (employee as { prospectID?: string }).prospectID ||
      ''
  );
}

function laborRowKey(labor: ForemanEmployeeLabor): string {
  const anyLabor = labor as ForemanEmployeeLabor & {
    foremanEmployeeLaborID?: string;
    employeeLaborID?: string;
    earnCode?: string;
    foremanPhaseID?: string;
    dateWorked?: string | Date;
    shift?: number;
  };
  const id = String(
    anyLabor.foremanEmployeeLaborID || anyLabor.employeeLaborID || ''
  );
  if (id) {
    return `id:${id}`;
  }
  // Pre-upgrade sparse shells often lack labor ids; keep both real entries by
  // phase/date/shift/earn rather than collapsing distinct WO rows into one.
  // 🔴 Do NOT put hours in this key — hours are mutable. Including them makes
  // 8→10 look like two rows (18h inflated payroll). Dropping hours lets the
  // higher-hours preference below actually run on the row: path.
  const date = anyLabor.dateWorked != null ? String(anyLabor.dateWorked) : '';
  return `row:${date}|${anyLabor.shift ?? ''}|${anyLabor.earnCode ?? ''}|${
    anyLabor.foremanPhaseID ?? ''
  }`;
}

function unionLabors(
  a: ForemanEmployeeLabor[] | undefined,
  b: ForemanEmployeeLabor[] | undefined
): ForemanEmployeeLabor[] {
  const map = new Map<string, ForemanEmployeeLabor>();
  const order: string[] = [];
  for (const labor of [...(a || []), ...(b || [])]) {
    const key = laborRowKey(labor);
    if (!map.has(key)) {
      order.push(key);
      map.set(key, labor);
      continue;
    }
    // Same labor id twice: prefer the row with more hours / isChanged.
    const existing = map.get(key)!;
    const preferB =
      Number(labor.hours || 0) > Number(existing.hours || 0) ||
      (labor.isChanged === true && existing.isChanged !== true);
    if (preferB) {
      map.set(key, labor);
    }
  }
  return order.map((k) => map.get(k)!);
}

function unionEmployees(
  a: ForemanEmployeeDispatch[] | undefined,
  b: ForemanEmployeeDispatch[] | undefined
): ForemanEmployeeDispatch[] {
  const map = new Map<string, ForemanEmployeeDispatch>();
  const order: string[] = [];
  let anon = 0;

  const ingest = (employee: ForemanEmployeeDispatch) => {
    let key = employeeDispatchKey(employee);
    if (!key) {
      anon += 1;
      key = `__anon_${anon}`;
    }
    if (!map.has(key)) {
      order.push(key);
      map.set(key, {
        ...employee,
        labors: [...(employee.labors || [])],
      } as ForemanEmployeeDispatch);
      return;
    }
    const existing = map.get(key)!;
    map.set(key, {
      ...existing,
      ...employee,
      labors: unionLabors(existing.labors, employee.labors),
    } as ForemanEmployeeDispatch);
  };

  for (const e of a || []) {
    ingest(e);
  }
  for (const e of b || []) {
    ingest(e);
  }
  return order.map((k) => map.get(k)!);
}

/**
 * Collapse two same-identity job shells into one WITHOUT dropping hours.
 *
 * 🔴 Pre-upgrade offline data often has hours split across dual-key shells.
 * Union labors from every shell; never discard a shell whole.
 */
export function mergeForemanJobsForDedupe(
  a: ForemanJobDetail,
  b: ForemanJobDetail
): ForemanJobDetail {
  const primary = identityRichness(b) > identityRichness(a) ? b : a;
  const secondary = primary === a ? b : a;
  const merged = deepCloneJob(primary);

  if (!merged.foremanJobID && secondary.foremanJobID) {
    merged.foremanJobID = secondary.foremanJobID;
  }
  if (!merged.jobID && secondary.jobID) {
    merged.jobID = secondary.jobID;
  }
  if (!merged.name && secondary.name) {
    merged.name = secondary.name;
  }
  if (!merged.vpJobID && secondary.vpJobID) {
    merged.vpJobID = secondary.vpJobID;
  }

  merged.employees = unionEmployees(primary.employees, secondary.employees);
  return merged;
}

/**
 * @deprecated Prefer mergeForemanJobsForDedupe — shell pick drops labors.
 * Kept as a thin alias so no path discards a shell.
 */
export function preferForemanJobForDedupe(
  a: ForemanJobDetail,
  b: ForemanJobDetail
): ForemanJobDetail {
  return mergeForemanJobsForDedupe(a, b);
}

/**
 * Collapse the jobs array so one logical job is one element.
 *
 * - **Keyless rows are KEPT.** A list filter may hide them; storage must not
 *   delete unsubmitted hours. Pre-fix `main` preserved every row.
 * - **Confluent.** Union-find over `jobsShareForemanIdentity` so A(F-only) +
 *   B(J-only) + C(F+J) collapses regardless of order (single-pass findIndex
 *   was not confluent and left #30176 unfixed on some device orderings).
 * - Same-identity shells are labor-union merged, never discarded whole.
 */
export function dedupeForemanJobsByIdentity(
  jobs: ForemanJobDetail[] | null | undefined
): ForemanJobDetail[] {
  if (!jobs?.length) {
    return [];
  }

  const n = jobs.length;
  const parent = Array.from({ length: n }, (_, i) => i);

  const find = (i: number): number => {
    let root = i;
    while (parent[root] !== root) {
      root = parent[root];
    }
    let cur = i;
    while (parent[cur] !== cur) {
      const next = parent[cur];
      parent[cur] = root;
      cur = next;
    }
    return root;
  };

  const unite = (i: number, j: number) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) {
      // Keep the lower index as root so first-appearance order stays stable.
      if (ri < rj) {
        parent[rj] = ri;
      } else {
        parent[ri] = rj;
      }
    }
  };

  for (let i = 0; i < n; i++) {
    // Keyless rows stay singletons — never merge, never drop.
    if (!getForemanJobDetailKey(jobs[i])) {
      continue;
    }
    for (let j = i + 1; j < n; j++) {
      if (!getForemanJobDetailKey(jobs[j])) {
        continue;
      }
      if (jobsShareForemanIdentity(jobs[i], jobs[j])) {
        unite(i, j);
      }
    }
  }

  // Transitive closure: identity is transitive through bridge records.
  // Pairwise share is not enough alone for A–B when only C links them; union
  // above only unites when share is direct. A shares C, B shares C ⇒ A and B
  // both unite to C's component. Good.
  // But A and B never share each other without C — only with C present.
  // Fix: also run a second pass linking via shared keys in the same component?
  // Actually A(F) and C(F,J) share F; B(J) and C(F,J) share J; unite(A,C) and
  // unite(B,C) puts A,B,C in one component. Order independent. Good.

  // Wait - jobsShareForemanIdentity(A,B) is false when A has only F and B only J.
  // unite(A,C) and unite(B,C) still works. Union-find is correct.

  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const root = find(i);
    const list = groups.get(root) || [];
    list.push(i);
    groups.set(root, list);
  }

  // Preserve first-appearance order of components (min index in group).
  const roots = [...groups.keys()].sort(
    (a, b) => Math.min(...groups.get(a)!) - Math.min(...groups.get(b)!)
  );

  const result: ForemanJobDetail[] = [];
  for (const root of roots) {
    const indices = groups
      .get(root)!
      .slice()
      .sort((a, b) => a - b);
    let merged = jobs[indices[0]];
    for (let k = 1; k < indices.length; k++) {
      merged = mergeForemanJobsForDedupe(merged, jobs[indices[k]]);
    }
    result.push(merged);
  }
  return result;
}

/** Exported for tests / migration invariant. */
export function sumLaborHoursOnJob(job: ForemanJobDetail): number {
  return laborHoursOnJob(job);
}

export function sumLaborHoursOnJobs(
  jobs: ForemanJobDetail[] | null | undefined
): number {
  return (jobs || []).reduce((sum, j) => sum + laborHoursOnJob(j), 0);
}
